import { Anchor, Card, DataButton } from "@sellar-io/components";
import {
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import React, { ReactElement, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import {
  ContextOrderBasketType,
  useSellarDistributionEligibility,
} from "../../../hooks";
import { CheckoutSelectedFulfillmentType } from "../StockList";
import { useStockListOrderSummaryProps } from "../useStockListOrderSummaryProps";
import { SignUpToAccessSticky } from "./Storefront/SignUpBanners/SignUpToAccessSticky";
import { StorefrontInformation } from "./Storefront/StorefrontInformation";
import { StorefrontSupplierBanner } from "./Storefront/StorefrontSupplierBanner/StorefrontSupplierBanner";
import { StorefrontSupplierSellarCreditBanner } from "./Storefront/StorefrontSupplierBanner/StorefrontSupplierSellarCreditBanner";
import { StorefrontSupplierCardPaymentWithDepositBanner } from "./Storefront/StorefrontSupplierBanner/StorefrontSupplierCardPaymentWithDepositBanner";
import { StorefrontDistributionBanner } from "./Storefront/StorefrontDistributionBanner/StorefrontDistributionBanner";
import { StorefrontMessageDialog } from "./Storefront/StorefrontDialogs/StorefrontMessagesDialog/StorefrontMessagesDialog";
import { Icon } from "../../../components";

export const CategoryPageLayout = ({
  supplier,
  supplierStorefront,
  supplierIsLoading,
  navBarHeightClass,
  children,
  order,
  onFulfillmentTypeChange,
  checkoutSelectedFulfillmentType,
  onEnterPostcode,
}: {
  children: ReactNode;
  onEnterPostcode?: () => void;
  navBarHeightClass?: string;
  supplierIsLoading: boolean;
  supplier: SupplierGetResponseType | undefined;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
  order?: ContextOrderBasketType | null;
  onFulfillmentTypeChange?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  checkoutSelectedFulfillmentType?: CheckoutSelectedFulfillmentType;
}): ReactElement => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const [messageDialogActive, setMessageDialogActive] = useState(false);

  const { isEligible } = useSellarDistributionEligibility(supplierStorefront);

  const buyerRelationshipId =
    supplierStorefront?.SupplierRetailerConnection?.RelationshipId;

  const containerTypeDeliveryChargeRules =
    supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ?? [];

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { price: orderTotal, discountAmount, subTotal },
    calculatedContainerTypeDeliveryOffers,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    containerTypeDeliveryChargeRules,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    freeDeliveryBasketSubtotal,
  });

  // Show simplified view when SD-only and buyer is not in delivery area
  if (!isEligible) {
    return (
      <>
        <StorefrontSupplierBanner
          supplier={supplier}
          isLoading={supplierIsLoading}
        />
        <div className="mx-auto w-full max-w-[1200px] px-4 py-6">
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-semibold text-gray-900">
                Sorry, {supplier?.Business.name} aren't available in your area
                yet
              </h3>
              <p className="text-sm text-gray-600">
                {supplier?.Business.name} does not currently offer delivery to
                your postcode. Contact their team to discuss delivery options.
              </p>
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://www.sellar.io/delivered-by-sellar"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about Delivered by Sellar
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
            </div>
            {onEnterPostcode && (
              <DataButton onClick={onEnterPostcode}>
                Enter your postcode
              </DataButton>
            )}
            {buyerRelationshipId && (
              <DataButton
                onClick={() => setMessageDialogActive(true)}
                className="w-fit"
                border={true}
              >
                <Icon icon={"MESSAGE_OUTLINE"} />
                Contact supplier
              </DataButton>
            )}
          </div>
        </div>
        {!!buyerRelationshipId && (
          <StorefrontMessageDialog
            relationshipId={buyerRelationshipId}
            active={messageDialogActive}
            onDismiss={() => setMessageDialogActive(false)}
            supplierName={supplier?.Business.name ?? ""}
          />
        )}
      </>
    );
  }

  return (
    <>
      <StorefrontSupplierBanner
        supplier={supplier}
        isLoading={supplierIsLoading}
      />

      {/* renders sellar credit/ card payment with deposit storefront info */}
      <AdditionalStorefrontBanner
        supplier={supplier}
        sdDeliveryArea={supplierStorefront?.sdDeliveryArea}
      />

      {!isDesktop && (
        <div
          className={
            "grid w-full max-w-[100vw] auto-cols-[calc(100%-2rem)] grid-flow-col gap-4 overflow-x-auto p-2 sm:auto-cols-[calc(50%-2rem)]"
          }
        >
          <StorefrontInformation
            supplierStorefront={supplierStorefront}
            currency={supplier?.currency ?? "gbp"}
            calculatedDeliveryOffers={calculatedContainerTypeDeliveryOffers}
            supplier={supplier}
            order={order}
            orderTotal={orderTotal}
            orderSubtotal={subTotal}
            orderDiscountAmount={discountAmount}
            onFulfillmentTypeChange={onFulfillmentTypeChange}
            checkoutSelectedFulfillmentType={checkoutSelectedFulfillmentType}
          />
        </div>
      )}
      <div
        className={twMerge(
          "mx-auto grid w-full grid-cols-3 gap-2 xl:max-w-[1200px]",
          isMobile && "flex flex-col",
        )}
      >
        {children}
        <div className={"col-span-1"}>
          {isDesktop && (
            <div
              className={"sticky z-10 max-h-min overflow-auto bg-gray-100 py-2"}
              style={{
                top: navBarHeightClass ?? "calc(var(--nav-height))",
              }}
            >
              <Card
                className={twMerge(
                  "bg-white",
                  "flex max-h-[calc(100vh_-_16px_-_var(--nav-height))] flex-col gap-4 overflow-auto p-4",
                )}
              >
                <StorefrontInformation
                  supplierStorefront={supplierStorefront}
                  currency={supplier?.currency ?? "gbp"}
                  calculatedDeliveryOffers={
                    calculatedContainerTypeDeliveryOffers
                  }
                  supplier={supplier}
                  order={order}
                  orderTotal={orderTotal}
                  orderSubtotal={subTotal}
                  orderDiscountAmount={discountAmount}
                  onFulfillmentTypeChange={onFulfillmentTypeChange}
                  checkoutSelectedFulfillmentType={
                    checkoutSelectedFulfillmentType
                  }
                />
              </Card>
            </div>
          )}
        </div>
        {supplierStorefront?.storefrontLinkType === "noPricing" && (
          <SignUpToAccessSticky supplier={supplier} />
        )}
      </div>
    </>
  );
};

const AdditionalStorefrontBanner = (parameters: {
  supplier: SupplierGetResponseType | undefined;
  sdDeliveryArea:
    | SupplierStorefrontV2GetResponseType["sdDeliveryArea"]
    | undefined;
}) => {
  const { supplier, sdDeliveryArea } = parameters;

  if (supplier?.onlyAllowCardPaymentWithDeposit) {
    return <StorefrontSupplierCardPaymentWithDepositBanner />;
  }

  if (supplier && sdDeliveryArea) {
    return <StorefrontDistributionBanner supplier={supplier} />;
  }

  if (!supplier?.hasOptedOutSellarCredit) {
    return (
      <StorefrontSupplierSellarCreditBanner
        supplierName={supplier?.Business.name ?? ""}
      />
    );
  }

  return null;
};
